import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublicSettings } from '@/hooks/usePublicSettings';
import { useConsent } from '@/contexts/ConsentContext';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function extractMeasurementIds(code: string): string[] {
  const ids = new Set<string>();
  const idPattern = /\b(?:G|AW|UA)-[A-Z0-9-]+\b/gi;
  for (const match of code.matchAll(idPattern)) {
    ids.add(match[0].toUpperCase());
  }
  return Array.from(ids);
}

function clearGoogleAnalyticsCookies() {
  const hostnameParts = window.location.hostname.split('.');
  const domains = ['', window.location.hostname];
  for (let index = 0; index < hostnameParts.length - 1; index += 1) {
    domains.push(`.${hostnameParts.slice(index).join('.')}`);
  }

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (!name || (!name.startsWith('_ga') && name !== '_gid' && name !== '_gat')) return;
    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
    });
  });
}

export function AnalyticsManager() {
  const { data: settings } = usePublicSettings();
  const { preferences } = useConsent();
  const location = useLocation();
  const analyticsCode = settings?.analyticsCode?.trim() ?? '';
  const measurementIds = useMemo(() => extractMeasurementIds(analyticsCode), [analyticsCode]);
  const injectedCodeRef = useRef<string>('');

  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Remove previously managed tags before re-injecting.
    document.head
      .querySelectorAll('[data-phx-analytics-managed="true"]')
      .forEach((node) => node.parentNode?.removeChild(node));

    if (!analyticsCode || !preferences.analytics) {
      injectedCodeRef.current = '';
      clearGoogleAnalyticsCookies();
      // Google defines gtag as a non-configurable global in some browsers.
      // Assignment safely disables it; deleting it can throw and unmount React.
      window.gtag = undefined;
      window.dataLayer = undefined;
      return;
    }
    if (analyticsCode === injectedCodeRef.current) {
      return;
    }

    const tpl = document.createElement('template');
    tpl.innerHTML = analyticsCode;
    const nodes = Array.from(tpl.content.childNodes);

    nodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as HTMLElement;

      if (element.tagName.toLowerCase() === 'script') {
        const oldScript = element as HTMLScriptElement;
        const next = document.createElement('script');
        for (const attr of oldScript.attributes) {
          next.setAttribute(attr.name, attr.value);
        }
        next.textContent = oldScript.textContent;
        next.setAttribute('data-phx-analytics-managed', 'true');
        document.head.appendChild(next);
        return;
      }

      const clone = element.cloneNode(true) as HTMLElement;
      clone.setAttribute('data-phx-analytics-managed', 'true');
      document.head.appendChild(clone);
    });

    injectedCodeRef.current = analyticsCode;
  }, [analyticsCode, preferences.analytics]);

  useEffect(() => {
    if (!preferences.analytics || !measurementIds.length) return;
    if (typeof window.gtag !== 'function') return;

    const pagePath = `${location.pathname}${location.search}`;
    measurementIds.forEach((id) => {
      window.gtag?.('config', id, {
        page_path: pagePath,
        page_location: window.location.href,
        page_title: document.title,
      });
    });
  }, [location.pathname, location.search, measurementIds, preferences.analytics]);

  return null;
}
